import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Compass,
  Bot,
  Code2,
  RefreshCcw,
  TrendingUp,
  ArrowUpRight,
} from "lucide-react";
import { services } from "@/data/services";

const icons = { Compass, Bot, Code2, RefreshCcw, TrendingUp };
const iconBySlug = {
  "ai-strategy-consulting": "Compass",
  "custom-ai-implementation": "Bot",
  "custom-development": "Code2",
  "digital-transformation": "RefreshCcw",
  "high-roi-marketing": "TrendingUp",
};

const spans = [
  "col-span-12 md:col-span-7 lg:col-span-8",
  "col-span-12 md:col-span-5 lg:col-span-4",
  "col-span-12 md:col-span-5 lg:col-span-4",
  "col-span-12 md:col-span-7 lg:col-span-8",
  "col-span-12",
];

const fadeUp = {
  initial: { opacity: 0, y: 30 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-80px" },
  transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] },
};

const Card = ({ service, span }) => {
  const Icon = icons[iconBySlug[service.slug]];
  return (
    <motion.div {...fadeUp} className={span}>
      <Link
        to={`/services/${service.slug}`}
        data-testid={`bento-card-${service.slug}`}
        className="bento-card group relative flex flex-col justify-between rounded-2xl border border-slate-200 bg-white p-7 lg:p-9 h-full"
      >
        <div>
          <div className="flex items-start justify-between mb-6">
            <div className="w-12 h-12 rounded-xl bg-[rgba(15,168,158,0.08)] border border-[rgba(15,168,158,0.2)] flex items-center justify-center">
              <Icon className="w-6 h-6 text-[#0FA89E]" />
            </div>
            <span className="font-mono-tech text-[10px] uppercase tracking-[0.2em] text-[#0FA89E] border border-[rgba(15,168,158,0.25)] rounded-full px-3 py-1">
              {service.badge}
            </span>
          </div>
          <h3 className="font-display text-xl sm:text-2xl font-semibold tracking-tight text-[#0F172A] mb-3">
            {service.title}
          </h3>
          <p className="text-base text-slate-600 leading-relaxed max-w-xl">
            {service.intro}
          </p>
        </div>

        <div className="mt-8">
          {service.image && span.includes("lg:col-span-8") && (
            <div className="rounded-xl overflow-hidden mb-6 h-40 lg:h-44 bg-white border border-slate-100">
              <img
                src={service.image}
                alt={service.title}
                loading="lazy"
                className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
              />
            </div>
          )}
          <div className="flex items-center justify-between border-t border-slate-100 pt-4">
            <span className="text-sm font-semibold text-[#0FA89E]">
              Explore this service
            </span>
            <ArrowUpRight className="w-5 h-5 text-[#0FA89E] transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
          </div>
        </div>
      </Link>
    </motion.div>
  );
};

export const Services = () => (
  <section
    id="services"
    data-testid="services-section"
    className="relative py-20 lg:py-32 bg-[#FAFAFC]"
  >
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <motion.div {...fadeUp} className="max-w-2xl mb-14 lg:mb-20">
        <p className="font-mono-tech text-xs uppercase tracking-[0.25em] text-[#0FA89E] font-semibold mb-4">
          What we do
        </p>
        <h2 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight text-[#0F172A] mb-5">
          Big Tech sells you the same box as everyone else
        </h2>
        <p className="text-lg text-slate-600 leading-relaxed">
          We open that box, take out what you don't need, and build what's
          missing. Custom tools, not force-fit compromises.
        </p>
      </motion.div>

      <div
        data-testid="bento-grid"
        className="grid grid-cols-1 md:grid-cols-12 gap-6 lg:gap-8"
      >
        {services.map((s, i) => (
          <Card key={s.slug} service={s} span={spans[i]} />
        ))}
      </div>
    </div>
  </section>
);
